// Cliente da API. Mesmo domínio (o Caddy serve app e /api), então o cookie de sessão vai
// sozinho. Toda escrita manda JSON — a API recusa outro formato (proteção contra CSRF).

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    credentials: "same-origin",
    headers: body === undefined ? {} : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new ApiError(res.status, data?.error ?? "erro", data?.message ?? `erro ${res.status}`);
  }
  return data as T;
}

export const api = {
  get: <T>(url: string) => request<T>("GET", url),
  post: <T = { ok: true }>(url: string, body?: unknown) => request<T>("POST", url, body ?? {}),
  patch: <T = { ok: true }>(url: string, body: unknown) => request<T>("PATCH", url, body),
  put: <T = { ok: true }>(url: string, body: unknown) => request<T>("PUT", url, body),
  del: <T = { ok: true }>(url: string) => request<T>("DELETE", url),
};

// ── Tipos das respostas ──────────────────────────────────────────────────────

export type Role = "platform_admin" | "group_manager" | "site_manager" | "viewer";

export const ROLE_LABEL: Record<Role, string> = {
  platform_admin: "Administrador da plataforma",
  group_manager: "Gestor de planta",
  site_manager: "Gestor de unidade",
  viewer: "Usuário",
};

export interface Branding {
  groupId: number | null;
  title: string;
  primaryColor: string | null;
  logoLight: boolean;
  logoDark: boolean;
  /** auto: segue o navegador; light/dark: forçado pelo grupo. */
  theme: ThemeMode;
}

export type ThemeMode = "auto" | "light" | "dark";

export interface Me {
  user: { id: number; email: string; name: string; totpEnabled: boolean };
  memberships: {
    id: number;
    role: Role;
    group: { id: number; name: string } | null;
    site: { id: number; name: string } | null;
    unit: { id: number; name: string } | null;
  }[];
  mfaSetupRequired: boolean;
  /** Entrou com senha temporária: precisa trocar antes de usar o sistema. */
  mustChangePassword: boolean;
  unitPrefs: UnitPrefs;
  /** Marca do grupo do usuário; null para quem não tem grupo (admin da plataforma). */
  branding: Branding | null;
}

export interface UnitPrefs {
  voltage?: "V" | "kV" | "MV";
  currentArm?: "A" | "kA";
  currentFld?: "A" | "kA";
  power?: "W" | "kW" | "MW";
  decimals?: number;
}

export interface TreeRegulator {
  id: number;
  slug: string;
  name: string;
  online: boolean | null;
  model: string | null;
  lastSeen: string | null;
}
export interface TreeUnit {
  id: number;
  slug: string;
  name: string;
  regulators: TreeRegulator[];
}
export interface TreeSite {
  id: number;
  slug: string;
  name: string;
  timezone: string;
  canManage: boolean;
  units: TreeUnit[];
}
export interface TreeGroup {
  id: number;
  slug: string;
  name: string;
  subdomain: string | null;
  /** Análises com IA por mês (0 = desligado). */
  analysisMonthlyLimit: number;
  canManage: boolean;
  sites: TreeSite[];
}
export interface Tree {
  groups: TreeGroup[];
  isAdmin: boolean;
  /** Domínio base do white label (<subdomínio>.<baseDomain>); vazio desliga. */
  baseDomain: string;
}

export interface MembershipRow {
  id: number;
  role: Role;
  groupId: number | null;
  siteId: number | null;
  unitId: number | null;
}
export interface AdminUser {
  id: number;
  email: string;
  name: string;
  disabled: boolean;
  totpEnabled: boolean;
  pendingInvite: boolean;
  /** Entra com senha temporária e ainda não trocou. */
  temporaryPassword: boolean;
  lastLoginAt: string | null;
  memberships: MembershipRow[];
  canManage: boolean;
}

export interface Source {
  site: string;
  ug: string;
  reg: string;
  first_seen: string;
  last_seen: string;
  last_topic: string;
  message_count: number;
}

export interface AuditEntry {
  ts: string;
  action: string;
  target: string | null;
  detail: unknown;
  ip?: string | null;
  email: string | null;
}

// ── Operação (Fase 3) ────────────────────────────────────────────────────────

export interface RegulatorDetail {
  id: number;
  slug: string;
  name: string;
  online: boolean | null;
  linkDetail: string | null;
  linkTs: string | null;
  model: string | null;
  lastSeen: string | null;
  unit: { id: number; name: string };
  site: { id: number; name: string; slug: string; timezone: string };
  group: { id: number; name: string };
  meta: { gatewayVersion: string; scaling: Record<string, number>; ts: string } | null;
  nominal: Nominal;
  /** O que foi cadastrado à mão (SI); chave ausente = automático. */
  nominalManual: Partial<Record<NominalKey, number>>;
}

/** Grandezas com nominal ajustável pelo admin. */
export type NominalKey = "voltage" | "current" | "power" | "fieldVoltage" | "fieldCurrent" | "speed";

export interface NominalValue {
  value: number;
  /** equipamento = primários de TP/TC lidos do regulador; fundo_escala = gateway antigo. */
  source: "manual" | "equipamento" | "fundo_escala" | "fixo";
}

export type Nominal = Record<NominalKey | "frequency", NominalValue | null>;

export interface DashboardRegulator {
  id: number;
  name: string;
  online: boolean | null;
  linkDetail: string | null;
  lastSeen: string | null;
  model: string | null;
  unit: { id: number; name: string };
  site: { id: number; name: string };
  group: { id: number; name: string };
  activeAlarms: number;
  nominal: Nominal;
  analog: Omit<LiveAnalog, "addr">[];
}

export interface LiveAnalog {
  tag: string;
  description: string;
  unit: string;
  base: string | null;
  addr: number | null;
  value: number;
  ts: string;
}

export interface LiveWord {
  addr: number;
  tag: string;
  description: string;
  word: number;
  ts: string;
  bits: { tag: string; bit: number | null; description: string; value: boolean }[];
}

export interface ActiveAlarm {
  word: number;
  bit: number;
  tag: string;
  description: string;
  grp: string;
  since: string;
  regulator_id?: number;
  regulator_name?: string;
  unit_name?: string;
  site_name?: string;
}

export interface Live {
  analog: LiveAnalog[];
  status: LiveWord[];
  activeAlarms: ActiveAlarm[];
}

export interface SignalInfo {
  tag: string;
  description: string;
  unit: string;
  base: string | null;
  addr: number | null;
}

export interface Series {
  resolution: "raw" | "1m" | "1h";
  series: (SignalInfo & { points: [number, number, number, number][] })[];
}

export interface AlarmHistoryRow {
  activated_at: string;
  cleared_at: string | null;
  word: number;
  bit: number;
  tag: string;
  description: string;
  grp: string;
  regulator_id: number;
  regulator_name: string;
  unit_name: string;
  site_name: string;
}
